import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { ArrowLeftIcon, PlusIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import api from '../../services/api.ts';
import { formatCurrency } from '../../utils/currency.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import FrotaVehicleTabs from '../../components/frota/FrotaVehicleTabs.tsx';

const FrotaManutencoes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [type, setType] = useState('all');
  const [period, setPeriod] = useState('12m');

  const { data, isLoading, error } = useQuery(
    ['fleet-maintenances', id, type, period],
    async () => {
      const params: any = {};
      if (type !== 'all') params.type = type;
      if (period === 'all') {
        params.from = '2000-01-01';
      }
      const res = await api.get(`/patrimonio/frota/${id}/manutencoes`, { params });
      return res.data;
    },
    { enabled: !!id }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }
  if (error || !data) {
    return <div className="text-center text-red-500">Erro ao carregar manutenções.</div>;
  }

  const { items, summary, vehicle: v } = data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <p className="text-sm text-gray-500">
            Patrimônio / Frota / {v?.licensePlate} / Manutenções
          </p>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center mt-1">
            <WrenchScrewdriverIcon className="h-8 w-8 mr-3 text-primary-600" />
            Manutenções — {v?.make} {v?.model}
          </h1>
        </div>
        {hasPermission('assets.create') && (
          <button
            onClick={() => navigate(`/patrimonio/frota/${id}/manutencoes/nova`)}
            className="bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-xl font-medium flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Nova manutenção
          </button>
        )}
      </div>

      {id && <FrotaVehicleTabs id={id} />}

      <div className="flex flex-wrap items-end gap-3">
        <button
          onClick={() => navigate(`/patrimonio/frota/${id}`)}
          className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center px-3 py-2 rounded-lg border border-gray-200"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" /> Detalhe do veículo
        </button>
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">Tipo</span>
          <select value={type} onChange={e => setType(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2">
            <option value="all">Todos</option>
            <option value="preventive">Preventiva</option>
            <option value="corrective">Corretiva</option>
          </select>
        </label>
        <label className="text-sm">
          <span className="text-gray-500 block mb-1">Período</span>
          <select value={period} onChange={e => setPeriod(e.target.value)} className="border border-gray-300 rounded-xl px-3 py-2">
            <option value="12m">Últimos 12 meses</option>
            <option value="all">Todo histórico</option>
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total gasto (12 meses)</p>
          <p className="text-2xl font-bold text-red-600 mt-1 tabular-nums">
            {formatCurrency(summary?.totalAmount || 0)}
          </p>
          <p className="text-xs text-gray-400 mt-1">{summary?.count || 0} serviços</p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Próxima preventiva</p>
          <p className="text-xl font-bold text-gray-900 mt-1 tabular-nums">
            {summary?.nextPreventiveKm != null
              ? `${Number(summary.nextPreventiveKm).toLocaleString('pt-BR')} km`
              : '—'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            {summary?.remainingKm != null
              ? `faltam ~${Number(summary.remainingKm).toLocaleString('pt-BR')} km`
              : 'sem meta'}
          </p>
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Status atual</p>
          <p className="text-lg font-bold text-green-700 mt-1">{summary?.statusLabel || '—'}</p>
          <p className="text-xs text-gray-400 mt-1">
            {summary?.lastDescription ? `última: ${summary.lastDescription}` : 'sem histórico'}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">Histórico de manutenções</h2>
          <p className="text-sm text-gray-500">
            Preventiva e corretiva · {v?.make} {v?.licensePlate}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Data</th>
                <th className="text-left px-5 py-3 font-medium">Tipo</th>
                <th className="text-left px-5 py-3 font-medium">Km</th>
                <th className="text-left px-5 py-3 font-medium">Descrição</th>
                <th className="text-left px-5 py-3 font-medium">Oficina</th>
                <th className="text-right px-5 py-3 font-medium">Valor</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-5 py-8 text-center text-gray-500">
                    Nenhuma manutenção neste período.
                  </td>
                </tr>
              ) : (
                items.map((m: any) => (
                  <tr key={m.id}>
                    <td className="px-5 py-3 font-mono">
                      {new Date(m.serviceDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          m.type === 'preventive'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {m.typeLabel}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono">
                      {m.kmAtService != null
                        ? `${Number(m.kmAtService).toLocaleString('pt-BR')} km`
                        : '—'}
                    </td>
                    <td className="px-5 py-3">{m.description}</td>
                    <td className="px-5 py-3">{m.workshop || '—'}</td>
                    <td className="px-5 py-3 text-right text-red-600 tabular-nums">
                      {formatCurrency(m.amount)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 text-blue-900 rounded-xl px-4 py-3 text-sm">
        Ao registrar manutenção com custo, opção de criar saída no <strong>Fluxo de caixa</strong>{' '}
        (categoria Veículo / Manutenção).{' '}
        <Link to="/financeiro/fluxo-de-caixa" className="font-medium underline">
          Abrir fluxo
        </Link>
      </div>
    </div>
  );
};

export default FrotaManutencoes;
